/**
 * OntologyGraphExplorer — an interactive, scalable visualiser for an ontology
 * version's entity / relation graph. Dependency-free (no d3 / cytoscape).
 *
 * Designed for enterprise-sized ontologies, not just the 10-40 node previews
 * that `OntologyMiniGraph` handles:
 *   - Force-directed layout (deterministic, seeded — stable across reloads).
 *   - Pan (drag background) + zoom (wheel / buttons).
 *   - Node drag to rearrange.
 *   - Type-ahead search that highlights and focuses a class.
 *   - Click-to-focus: isolate a node's 1-hop neighbourhood so a 1000-node
 *     graph stays legible — you drill in instead of rendering everything.
 *   - Degree-based node sizing so hub classes stand out.
 *
 * Data shape matches `GET /api/v1/ontology/versions/<id>/graph/`:
 *   { nodes: [{id, label, parent?, description?}],
 *     edges: [{id, label, source, target, cardinality?}] }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, Maximize2, Minus, Plus, RotateCcw, Search, X } from "lucide-react";

interface Node { id: string; label?: string; parent?: string; description?: string }
interface Edge { id?: string; label: string; source: string; target: string; cardinality?: string }

export interface OntologyGraph {
  nodes: Node[];
  edges: Edge[];
}

interface Props {
  graph: OntologyGraph;
  height?: number;
}

interface Pt { x: number; y: number }

/** Deterministic pseudo-random in [0,1) from a string seed. */
function seeded(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // xorshift
  h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * Lightweight force-directed layout. O(n^2) repulsion per tick, so iterations
 * are scaled down for big graphs (focus mode keeps those legible anyway).
 */
function layout(nodes: Node[], edges: Edge[], W: number, H: number): Map<string, Pt> {
  const pos = new Map<string, Pt>();
  const n = nodes.length;
  if (n === 0) return pos;

  // Seed on a circle (deterministic) so the sim converges the same way each load.
  nodes.forEach((nd, i) => {
    const a = (i / n) * Math.PI * 2 + seeded(nd.id) * 0.5;
    const r = Math.min(W, H) * 0.35 * (0.6 + 0.4 * seeded(nd.id + "r"));
    pos.set(nd.id, { x: W / 2 + Math.cos(a) * r, y: H / 2 + Math.sin(a) * r });
  });

  if (n === 1) { pos.set(nodes[0].id, { x: W / 2, y: H / 2 }); return pos; }

  const ids = nodes.map((nd) => nd.id);
  const idx = new Map(ids.map((id, i) => [id, i]));
  const links = edges
    .map((e) => [idx.get(e.source), idx.get(e.target)] as [number | undefined, number | undefined])
    .filter((l): l is [number, number] => l[0] !== undefined && l[1] !== undefined && l[0] !== l[1]);

  const iterations = n > 600 ? 60 : n > 250 ? 120 : 250;
  const k = Math.sqrt((W * H) / n);        // ideal edge length
  const repulse = k * k * 0.9;
  let temp = W / 10;
  const cool = temp / (iterations + 1);

  const disp = ids.map(() => ({ x: 0, y: 0 }));

  for (let it = 0; it < iterations; it++) {
    for (let i = 0; i < n; i++) { disp[i].x = 0; disp[i].y = 0; }

    // Repulsion (all pairs).
    for (let i = 0; i < n; i++) {
      const pi = pos.get(ids[i])!;
      for (let j = i + 1; j < n; j++) {
        const pj = pos.get(ids[j])!;
        let dx = pi.x - pj.x, dy = pi.y - pj.y;
        let dist = Math.hypot(dx, dy) || 0.01;
        if (dist > k * 6) continue;        // ignore far pairs (perf + looser layout)
        const force = repulse / dist;
        dx = (dx / dist) * force; dy = (dy / dist) * force;
        disp[i].x += dx; disp[i].y += dy;
        disp[j].x -= dx; disp[j].y -= dy;
      }
    }

    // Attraction along edges.
    for (const [a, b] of links) {
      const pa = pos.get(ids[a])!, pb = pos.get(ids[b])!;
      let dx = pa.x - pb.x, dy = pa.y - pb.y;
      const dist = Math.hypot(dx, dy) || 0.01;
      const force = (dist * dist) / k;
      dx = (dx / dist) * force; dy = (dy / dist) * force;
      disp[a].x -= dx; disp[a].y -= dy;
      disp[b].x += dx; disp[b].y += dy;
    }

    // Apply with temperature cap + gentle gravity toward centre.
    for (let i = 0; i < n; i++) {
      const p = pos.get(ids[i])!;
      const d = Math.hypot(disp[i].x, disp[i].y) || 0.01;
      p.x += (disp[i].x / d) * Math.min(d, temp);
      p.y += (disp[i].y / d) * Math.min(d, temp);
      p.x += (W / 2 - p.x) * 0.01;
      p.y += (H / 2 - p.y) * 0.01;
    }
    temp -= cool;
  }
  return pos;
}

export function OntologyGraphExplorer({ graph, height = 520 }: Props) {
  const W = 1200;
  const H = 900;

  const nodes = graph.nodes ?? [];
  const allowed = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);
  const edges = useMemo(
    () => (graph.edges ?? []).filter((e) => allowed.has(e.source) && allowed.has(e.target)),
    [graph.edges, allowed],
  );

  // Degree + adjacency (for sizing + focus).
  const degree = useMemo(() => {
    const d = new Map<string, number>();
    for (const n of nodes) d.set(n.id, 0);
    for (const e of edges) {
      d.set(e.source, (d.get(e.source) ?? 0) + 1);
      d.set(e.target, (d.get(e.target) ?? 0) + 1);
    }
    return d;
  }, [nodes, edges]);

  const adj = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const n of nodes) m.set(n.id, new Set());
    for (const e of edges) {
      m.get(e.source)?.add(e.target);
      m.get(e.target)?.add(e.source);
    }
    return m;
  }, [nodes, edges]);

  const basePos = useMemo(() => layout(nodes, edges, W, H), [nodes, edges]);

  // Live positions (so node drag is possible without recomputing the sim).
  const [positions, setPositions] = useState<Map<string, Pt>>(basePos);
  useEffect(() => setPositions(basePos), [basePos]);

  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [query, setQuery] = useState("");
  const [focusId, setFocusId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<{ kind: "pan" | "node"; id?: string; sx: number; sy: number; ox: number; oy: number } | null>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return new Set<string>();
    return new Set(nodes.filter((n) => (n.label ?? n.id).toLowerCase().includes(q)).map((n) => n.id));
  }, [query, nodes]);

  // Focus set: the focused node + its 1-hop neighbours (or everything).
  const visible = useMemo(() => {
    if (!focusId) return null;
    const s = new Set<string>([focusId]);
    adj.get(focusId)?.forEach((x) => s.add(x));
    return s;
  }, [focusId, adj]);

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    const k = Math.min(4, Math.max(0.2, view.k * factor));
    const rect = svgRef.current!.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width * W;
    const my = (e.clientY - rect.top) / rect.height * H;
    // Zoom toward cursor.
    setView((v) => ({
      k,
      x: mx - ((mx - v.x) / v.k) * k,
      y: my - ((my - v.y) / v.k) * k,
    }));
  }

  function onPointerDown(e: React.PointerEvent, id?: string) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (id) {
      const p = positions.get(id)!;
      drag.current = { kind: "node", id, sx: e.clientX, sy: e.clientY, ox: p.x, oy: p.y };
    } else {
      drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const rect = svgRef.current!.getBoundingClientRect();
    const dx = (e.clientX - d.sx) / rect.width * W;
    const dy = (e.clientY - d.sy) / rect.height * H;
    if (d.kind === "pan") {
      setView((v) => ({ ...v, x: d.ox + dx, y: d.oy + dy }));
    } else if (d.id) {
      setPositions((prev) => {
        const next = new Map(prev);
        next.set(d.id!, { x: d.ox + dx / view.k, y: d.oy + dy / view.k });
        return next;
      });
    }
  }

  function onPointerUp() { drag.current = null; }

  function resetView() { setView({ x: 0, y: 0, k: 1 }); setFocusId(null); }

  function focusOn(id: string) {
    setFocusId(id);
    const p = positions.get(id);
    if (p) setView({ k: 1.4, x: W / 2 - p.x * 1.4, y: H / 2 - p.y * 1.4 });
  }

  if (nodes.length === 0) {
    return (
      <div className="rounded border border-border bg-bg/50 p-8 text-center text-xs text-muted">
        No entities to visualise yet.
      </div>
    );
  }

  const maxDeg = Math.max(1, ...Array.from(degree.values()));
  const nodeR = (id: string) => 14 + 14 * Math.sqrt((degree.get(id) ?? 0) / maxDeg);

  const shownEdges = visible
    ? edges.filter((e) => visible.has(e.source) && visible.has(e.target))
    : edges;

  function isDim(id: string): boolean {
    if (visible) return !visible.has(id);
    if (matches.size) return !matches.has(id);
    return false;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-bg/50">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
          <input
            className="input h-7 w-48 pl-7 text-xs"
            placeholder="Search a class…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
                    onClick={() => setQuery("")} aria-label="Clear">
              <X size={12} />
            </button>
          )}
        </div>

        {/* Search results jump-list */}
        {query && matches.size > 0 && (
          <div className="flex max-w-[40%] flex-wrap gap-1">
            {nodes.filter((n) => matches.has(n.id)).slice(0, 6).map((n) => (
              <button
                key={n.id}
                type="button"
                className="pill text-[11px] hover:border-accent"
                onClick={() => focusOn(n.id)}
                title="Focus this class and its neighbours"
              >
                {n.label ?? n.id}
              </button>
            ))}
            {matches.size > 6 && <span className="self-center text-[11px] text-muted">+{matches.size - 6} more</span>}
          </div>
        )}

        <div className="ml-auto flex items-center gap-1">
          {focusId && (
            <button type="button" className="btn inline-flex items-center gap-1 text-xs" onClick={() => setFocusId(null)}>
              <Maximize2 size={12} /> Show all
            </button>
          )}
          <button type="button" className="btn p-1" title="Zoom in"
                  onClick={() => setView((v) => ({ ...v, k: Math.min(4, v.k * 1.2) }))}>
            <Plus size={13} />
          </button>
          <button type="button" className="btn p-1" title="Zoom out"
                  onClick={() => setView((v) => ({ ...v, k: Math.max(0.2, v.k / 1.2) }))}>
            <Minus size={13} />
          </button>
          <button type="button" className="btn p-1" title="Reset view" onClick={resetView}>
            <RotateCcw size={13} />
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ height }}
        className="block w-full cursor-grab touch-none select-none active:cursor-grabbing"
        role="img"
        aria-label="Interactive ontology graph"
        onWheel={onWheel}
        onPointerDown={(e) => onPointerDown(e)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <defs>
          <marker id="og-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="currentColor" className="text-muted" />
          </marker>
          <radialGradient id="og-node" cx="32%" cy="26%" r="82%">
            <stop offset="0%" stopColor="hsl(var(--accent) / 0.55)" />
            <stop offset="100%" stopColor="hsl(var(--accent) / 0.13)" />
          </radialGradient>
          <radialGradient id="og-node-focus" cx="32%" cy="26%" r="82%">
            <stop offset="0%" stopColor="hsl(var(--accent) / 0.95)" />
            <stop offset="100%" stopColor="hsl(var(--accent) / 0.4)" />
          </radialGradient>
          <radialGradient id="og-node-match" cx="32%" cy="26%" r="82%">
            <stop offset="0%" stopColor="hsl(var(--good) / 0.8)" />
            <stop offset="100%" stopColor="hsl(var(--good) / 0.2)" />
          </radialGradient>
          <filter id="og-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feDropShadow dx="0" dy="2" stdDeviation="5" floodColor="hsl(var(--accent))" floodOpacity="0.28" />
          </filter>
          <pattern id="og-grid" width="36" height="36" patternUnits="userSpaceOnUse">
            <circle cx="1.6" cy="1.6" r="1.6" fill="currentColor" />
          </pattern>
        </defs>

        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {/* Dot-grid backdrop — pans and zooms with the canvas. */}
          <rect x={-W} y={-H} width={W * 3} height={H * 3}
                fill="url(#og-grid)" className="text-border" opacity={0.55} />

          {/* Edges */}
          <g className="text-muted" stroke="currentColor" fill="none" strokeLinecap="round">
            {shownEdges.map((e, i) => {
              const a = positions.get(e.source); const b = positions.get(e.target);
              if (!a || !b) return null;
              if (e.source === e.target) {
                return (
                  <path key={i} d={`M ${a.x + 14} ${a.y - 8} a 16 16 0 1 1 -3 16`}
                        markerEnd="url(#og-arrow)" opacity={isDim(e.source) ? 0.08 : 0.5} strokeWidth={1.4} />
                );
              }
              const dx = b.x - a.x, dy = b.y - a.y;
              const len = Math.hypot(dx, dy) || 1;
              const ux = dx / len, uy = dy / len;
              const ra = nodeR(e.source), rb = nodeR(e.target);
              const highlighted = focusId != null && (e.source === focusId || e.target === focusId);
              const dim = isDim(e.source) && isDim(e.target);
              return (
                <line key={i}
                      x1={a.x + ux * ra} y1={a.y + uy * ra}
                      x2={b.x - ux * (rb + 5)} y2={b.y - uy * (rb + 5)}
                      markerEnd="url(#og-arrow)"
                      className={highlighted ? "text-accent" : "text-muted"}
                      strokeWidth={highlighted ? 1.8 : 1.2}
                      opacity={dim ? 0.07 : highlighted ? 0.85 : 0.4} />
              );
            })}
          </g>

          {/* Edge labels — only when zoomed-in or focused, to avoid clutter. */}
          {(view.k >= 1.1 || focusId) && (
            <g className="text-muted">
              {shownEdges.filter((e) => e.source !== e.target).map((e, i) => {
                const a = positions.get(e.source); const b = positions.get(e.target);
                if (!a || !b) return null;
                if (isDim(e.source) && isDim(e.target)) return null;
                return (
                  <text key={i} x={(a.x + b.x) / 2} y={(a.y + b.y) / 2}
                        textAnchor="middle" dominantBaseline="central"
                        fontSize="9.5" letterSpacing="0.3"
                        style={{
                          fill: "hsl(var(--muted))",
                          paintOrder: "stroke",
                          stroke: "hsl(var(--bg))",
                          strokeWidth: 3.5,
                          strokeLinejoin: "round",
                        }}>
                    {e.label.length > 22 ? e.label.slice(0, 21) + "…" : e.label}
                  </text>
                );
              })}
            </g>
          )}

          {/* Nodes */}
          <g>
            {nodes.map((n) => {
              const p = positions.get(n.id); if (!p) return null;
              const r = nodeR(n.id);
              const dim = isDim(n.id);
              const isMatch = matches.has(n.id);
              const isFocus = focusId === n.id;
              const deg = degree.get(n.id) ?? 0;
              const label = n.label ?? n.id;
              const shown = label.length > 22 ? label.slice(0, 21) + "…" : label;
              return (
                <g key={n.id} transform={`translate(${p.x} ${p.y})`}
                   opacity={dim ? 0.16 : 1}
                   className="cursor-pointer"
                   onPointerDown={(ev) => { ev.stopPropagation(); onPointerDown(ev, n.id); }}
                   onClick={(ev) => { ev.stopPropagation(); focusOn(n.id); }}>
                  <title>{label}{deg ? ` · ${deg} connection${deg === 1 ? "" : "s"}` : ""}</title>
                  {/* Soft ring halo */}
                  <circle r={r + 3}
                          fill="none"
                          className={isFocus ? "stroke-accent" : isMatch ? "stroke-good" : "stroke-accent"}
                          strokeWidth={1}
                          opacity={isFocus || isMatch ? 0.45 : 0.12} />
                  <circle r={r}
                          fill={isFocus ? "url(#og-node-focus)" : isMatch ? "url(#og-node-match)" : "url(#og-node)"}
                          className={isFocus ? "stroke-accent" : isMatch ? "stroke-good" : "stroke-accent/70"}
                          strokeWidth={isFocus || isMatch ? 2.5 : 1.4}
                          filter="url(#og-glow)" />
                  {/* Specular highlight for depth */}
                  <ellipse cx={-r * 0.28} cy={-r * 0.34} rx={r * 0.42} ry={r * 0.26}
                           fill="hsl(var(--text))" opacity={0.10} className="pointer-events-none" />
                  {/* Label below the node with a bg-coloured halo so it reads in any theme */}
                  <text textAnchor="middle" y={r + 15}
                        fontSize={13} letterSpacing="0.2"
                        className="pointer-events-none font-semibold"
                        style={{
                          fill: "hsl(var(--text))",
                          paintOrder: "stroke",
                          stroke: "hsl(var(--bg))",
                          strokeWidth: 4,
                          strokeLinejoin: "round",
                        }}>
                    {shown}
                  </text>
                </g>
              );
            })}
          </g>
        </g>
      </svg>

      {/* Footer / legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3 py-1.5 text-[11px] text-muted">
        <span>{nodes.length} entit{nodes.length === 1 ? "y" : "ies"} · {edges.length} relation{edges.length === 1 ? "" : "s"}</span>
        {focusId && (
          <span className="inline-flex items-center gap-1 text-accent">
            <Crosshair size={11} /> focused on {nodes.find((n) => n.id === focusId)?.label ?? focusId}
            {visible ? ` (+${visible.size - 1} neighbours)` : ""}
          </span>
        )}
        <span className="ml-auto">drag to pan · scroll to zoom · click a class to focus · node size = degree</span>
      </div>
    </div>
  );
}
